// ============================================================
// WHICH FIGURE a character's body plate draws (plan body-map, P1; rulings
// 2026-10-09). Derived from the sheet every time, never stored - so the look
// can never drift from the traits it is meant to follow.
//
//   a vehicle by its kind, the amorphous mass as goo (their bodies, not a person's)
//   else the GM's pin (engine/figureKind) - the only way to the Robot and the Daemon
//   else by precedence: Quadruped > Astartes > Machine > human ("the shape of the body wins; the rest is auras")
//   then the Xenos flag picks the alien version, and Multiple Arms / Crawler the variant, where the figure has one
//   a horde is a crowd of what its members would be
//
// Statblocks may carry Astartes, Machine and Xenos only as pins on the projected Target, so those are read off the
// Target; Quadruped, Multiple Arms and Crawler exist only as sheet traits.
//
// Pure. Zero React dependency.
// ============================================================

import { traitIdsOf } from '../battlefield/traitRules';
import { isCrowd, resolveBodyShape } from '../bodyShape';
import { FIGURE_LABELS, readFigure } from '../figureKind';
import { VEHICLE_KIND_LABELS, vehicleKindOf, type VehicleKind } from '../vehicleKind';
import type { PlateFigureId } from './figureArt';

/** What the derivation reads off the projected Target. */
export interface FigureTarget {
  astartes: boolean;
  machineAP: number;
  xenos?: boolean;
  bodyType?: string;
  horde?: { isHorde?: boolean };
  vehicleKind?: VehicleKind | null;
  figure?: unknown;
}

type Plain = 'human' | 'astartes' | 'beast' | 'machine';

/** A base figure's variants - the alien version, four arms, a tail or tracks, both - each only where one was drawn. */
interface Variants {
  xenos?: PlateFigureId;
  arms?: PlateFigureId;
  crawl?: PlateFigureId;
  both?: PlateFigureId;
}
const VARIANTS: Record<Plain, Variants> = {
  human: {},
  astartes: {},
  beast: { xenos: 'xenobeast' },
  machine: { arms: 'machine4a', crawl: 'machinetrack', both: 'machinetrack4a' },
};
const XENOS_VARIANTS: Variants = { arms: 'xenos4a', crawl: 'xenostail', both: 'xenostail4a' };
/** Which variant the body traits ask for, by which of them it has. */
const VARIANT_KEY: Record<string, keyof Variants> = { a: 'arms', c: 'crawl', ac: 'both' };

type PlateBody =
  | { kind: 'person'; figure: PlateFigureId }
  | { kind: 'vehicle'; vehicle: VehicleKind }
  | { kind: 'goo' }
  | { kind: 'object' };
export type PlateFigure = PlateBody & {
  /** A horde: a crowd of this figure. */
  crowd: boolean;
  /** The GM pinned it (else Automatic). */
  pinned: boolean;
};

/** The plain human: what a view shows with nobody to show. */
export const NOBODY: PlateFigure = { kind: 'person', figure: 'human', crowd: false, pinned: false };

function plainOf(target: FigureTarget, traits: Set<string>): Plain {
  if (traits.has('quadruped')) return 'beast';
  if (target.astartes) return 'astartes';
  return target.machineAP > 0 || traits.has('machine') ? 'machine' : 'human';
}

/** The variant a figure's table holds for these traits (four arms, a tail or tracks, both), if it holds one. */
function variantOf(table: Variants, traits: Set<string>): PlateFigureId | undefined {
  const key = VARIANT_KEY[(traits.has('multiple_arms') ? 'a' : '') + (traits.has('crawler') ? 'c' : '')];
  return key ? table[key] : undefined;
}

/** The person figure the traits give: the base, then the alien version, then the variant. */
function personOf(target: FigureTarget, traits: Set<string>): PlateFigureId {
  const plain = plainOf(target, traits);
  if (target.xenos && plain === 'human') return variantOf(XENOS_VARIANTS, traits) ?? 'xenos';
  return variantOf(VARIANTS[plain], traits) ?? ((target.xenos && VARIANTS[plain].xenos) || plain);
}

/** The figure a character's plate draws, from its projected Target and (where there is one) its sheet. */
export function figureFor(target: FigureTarget, sheet: { traits?: { traitId?: string }[] } | null): PlateFigure {
  const crowd = isCrowd(target);
  const shape = resolveBodyShape(target);
  if (shape === 'vehicle') return { kind: 'vehicle', vehicle: vehicleKindOf(target), crowd, pinned: false };
  if (shape === 'amorphous') return { kind: 'goo', crowd, pinned: false };
  // An Object is a thing, never a crowd (2026-10-26): its own plate, the diamond.
  if (shape === 'object') return { kind: 'object', crowd: false, pinned: false };
  const pin = readFigure(target.figure);
  if (pin) return { kind: 'person', figure: pin, crowd, pinned: true };
  return { kind: 'person', figure: personOf(target, new Set(traitIdsOf(sheet))), crowd, pinned: false };
}

/** What a plate shows, in words: the sheet's "Automatic (…)" and the plate's caption. */
export function figureLabel(f: PlateFigure): string {
  if (f.kind === 'vehicle') return VEHICLE_KIND_LABELS[f.vehicle];
  if (f.kind === 'goo') return 'Amorphous mass';
  if (f.kind === 'object') return 'Object';
  return FIGURE_LABELS[f.figure];
}
